/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { TableRow, TableCell, IconButton } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/system';
import {
  BUTTON_ICON_STYLE,
  EXPAND_BUTTON_BOX_STYLE,
  EXPAND_BUTTON_STYLE,
  ROW_STYLE,
} from './style';

/**
 * Recursive row component (supports infinite depth)
 */
export const ExpandableBodyRow = ({
  row,
  flatColumns,
  expandedRowIds,
  setExpandedRowIds,
  rowIndex,
  primaryRowColor,
  secondaryRowColor,
  primaryTextColor,
  secondaryTextColor,
  customRowStyle,
  renderCellValue,
  level = 0,
  rowOnClick,
  expandableKeys = [],
  isDrillDown = false,
  parentData = null,
  isDataLoading = false,
}) => {
  const value = expandableKeys
    .map((key) => `${row?.[key] ?? ''}_${level}`)
    .filter(Boolean)
    .join('');

  const isExpanded = expandedRowIds.has(value);

  const handleExpand = () => {
    if (isDrillDown) {
      return;
    }
    if (!row?.children && rowOnClick) {
      rowOnClick(row, level, parentData);
    }
    setExpandedRowIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) newSet.delete(value);
      else newSet.add(value);
      return newSet;
    });
  };

  let isAvailableExpandableButton = true;
  if (row.Continent === 'Total' || row.Country === 'Total' || row.State === 'Total') {
    isAvailableExpandableButton = false;
  } else if (row?.Continent) {
    isAvailableExpandableButton = level < 3;
  } else if (row?.Country) {
    isAvailableExpandableButton = level < 2;
  } else {
    isAvailableExpandableButton = level < 1;
  }

  const handleRowClick = () => {
    if (isDataLoading || !isAvailableExpandableButton || expandedRowIds.has(value)) {
      return;
    }
    if (rowOnClick && isDrillDown) {
      rowOnClick(row);
    }
  };
  return (
    <>
      {/* Main Row */}
      <TableRow key={value} onClick={handleRowClick}>
        {flatColumns.map((col, index) => (
          <TableCell
            key={`${col.id}_${row?.Continent || row?.Country || row?.State}_${rowIndex}`}
            align={col.align || 'left'}
            sx={{
              ...ROW_STYLE,
              ...customRowStyle,
              bgcolor: rowIndex % 2 === 0 ? primaryRowColor : secondaryRowColor,
              color: parseFloat(row?.[col?.id]) < 0 ? secondaryTextColor : primaryTextColor,
              fontWeight:
                expandedRowIds.has(value) && isAvailableExpandableButton ? 'bold' : 'normal',
              paddingLeft: index === 0 ? level * 4 : undefined,
              cursor:
                isAvailableExpandableButton && isDrillDown && index === 0 ? 'pointer' : 'default',
              '&:hover':
                isAvailableExpandableButton && isDrillDown && index === 0
                  ? {
                      backgroundColor: '#cccccc',
                    }
                  : {},
            }}
          >
            {isAvailableExpandableButton && index === 0 ? (
              <Box display="flex" alignItems="center" textAlign="right">
                <Box sx={EXPAND_BUTTON_BOX_STYLE}>
                  <IconButton
                    size="small"
                    sx={EXPAND_BUTTON_STYLE}
                    onClick={handleExpand}
                    disabled={isDataLoading}
                  >
                    {isExpanded ? (
                      <RemoveIcon sx={BUTTON_ICON_STYLE} />
                    ) : (
                      <AddIcon sx={BUTTON_ICON_STYLE} />
                    )}
                  </IconButton>
                </Box>
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  {renderCellValue(row?.[col?.id], col?.type)}
                </Box>
              </Box>
            ) : (
              renderCellValue(row?.[col?.id], col?.type)
            )}
          </TableCell>
        ))}
      </TableRow>

      {/* Children (recursive) */}
      {isExpanded &&
        row?.children?.map((child, index) => (
          <ExpandableBodyRow
            key={child.id}
            parentData={parentData ?? row}
            row={child}
            flatColumns={flatColumns}
            expandedRowIds={expandedRowIds}
            setExpandedRowIds={setExpandedRowIds}
            primaryRowColor={primaryRowColor}
            secondaryRowColor={secondaryRowColor}
            primaryTextColor={primaryTextColor}
            secondaryTextColor={secondaryTextColor}
            customRowStyle={customRowStyle}
            renderCellValue={renderCellValue}
            level={level + 1}
            rowOnClick={rowOnClick}
            expandableKeys={expandableKeys}
            rowIndex={index}
            isDrillDown={isDrillDown}
          />
        ))}
    </>
  );
};

export default ExpandableBodyRow;
